// Which garment did the URL ask for? — the pure half.
//
// ENG-1108's first pass is LOCAL: a garment is a file served out of
// `public/garments/`, named by `?garment=<name>`, exactly as the cloth
// deformer's `bundleUrl` and the OGS bundles are reached locally today. There
// is no garment asset kind on the server yet, so there is no id to ask for and
// nothing is persisted — which the caller must SAY, not imply (the badge in
// GarmentIndicator). When the asset kind lands, this module is the one place
// that changes: the rest of the pipeline takes a URL.
//
// The name is not a path. It is pasted into a URL by a human, so it is
// validated rather than trusted: a name with a slash, a backslash, a `..` or a
// scheme is refused outright instead of being cleaned up into something that
// happens to resolve. Silently repairing a bad name is how a traversal becomes
// a feature.
//
// No three.js import here, so node:test drives the rules.

/** Where a locally-served garment lives. Gitignored; see public/garments/README.md. */
export const GARMENT_DIR = '/garments'

/** Extensions the loader can actually parse. Marvelous writes all three. */
export const GARMENT_EXTENSIONS = Object.freeze(['glb', 'gltf'])

const NAME_OK = /^[A-Za-z0-9][A-Za-z0-9._-]*$/

/**
 * Resolve `?garment=<name>` into a URL to fetch.
 *
 * @param {string|URLSearchParams} search  the query string, or its parsed form
 * @returns {{ requested: false } |
 *           { requested: true, ok: true, name: string, url: string } |
 *           { requested: true, ok: false, name: string, error: string }}
 *   `requested: false` is the ordinary case — no flag, no garment, no message.
 *   A BAD name is `requested: true, ok: false`: the user asked for something
 *   and must be told why nothing appeared.
 */
export function resolveGarmentRequest(search) {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search
  const raw = params?.get?.('garment')
  if (raw == null || raw === '') return { requested: false }

  const name = raw.trim()
  if (!NAME_OK.test(name)) {
    return {
      requested: true,
      ok: false,
      name,
      error: `"${name}" is not a garment name. Expected a bare filename from public${GARMENT_DIR}/ `
        + '(letters, digits, dot, dash, underscore) — not a path or a URL.',
    }
  }

  const dot = name.lastIndexOf('.')
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
  if (ext && !GARMENT_EXTENSIONS.includes(ext)) {
    return {
      requested: true,
      ok: false,
      name,
      error: `"${name}" is a .${ext}; this loader reads ${GARMENT_EXTENSIONS.map((e) => `.${e}`).join(' or ')}. `
        + 'Export from Marvelous Designer as glTF.',
    }
  }
  // A bare name means the usual case: the .glb sibling.
  const file = ext ? name : `${name}.${GARMENT_EXTENSIONS[0]}`
  return { requested: true, ok: true, name, source: 'local', url: `${GARMENT_DIR}/${file}` }
}

/**
 * Resolve a garment request against the character's OWN garment assets first,
 * falling back to the local `public/garments/` file.
 *
 * The server row is the real source — the local directory only ever existed so
 * the importer could be built before the asset kind did (and it stays for dev,
 * where dropping a file in a folder beats a round trip). A request that names
 * an asset is answered with the URL THE SERVER GAVE, verbatim: `fileUrl` is
 * already the same-origin proxy path, and nothing here builds one (ENG-999).
 *
 * Matched by id first, then by exact name, then by name without its extension —
 * so `?garment=<uuid>`, `?garment=tshirt` and the filename a user sees on the
 * card all reach the same row.
 *
 * @param {string|URLSearchParams} search
 * @param {Array<{id:string,name?:string,fileUrl?:string,file_url?:string}>} [assets]
 */
export function resolveGarmentSource(search, assets = []) {
  const req = resolveGarmentRequest(search)
  if (!req.requested || !req.ok) return req

  const wanted = req.name
  const stem = wanted.replace(/\.[^.]+$/, '')
  const row = assets.find(a => a.id === wanted)
    ?? assets.find(a => a.name === wanted)
    ?? assets.find(a => a.name === stem)
    ?? assets.find(a => (a.name ?? '').replace(/\.[^.]+$/, '') === stem)
  if (!row) return req

  const url = row.fileUrl ?? row.file_url
  if (!url) {
    // A row with no file is a server-side fault, not something to paper over
    // with the local path — that would silently render a different file than
    // the card the user clicked.
    return {
      requested: true,
      ok: false,
      name: wanted,
      error: `garment asset "${wanted}" has no file URL — the row exists but its upload did not complete.`,
    }
  }
  return { requested: true, ok: true, name: row.name ?? wanted, source: 'asset', assetId: row.id, url }
}
